import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Immediate Meeting Interface - completely separate from scheduled meetings
 * These are "Zoom Now" style meetings without time slots
 */
export interface ImmediateMeeting {
  id: string;
  mentorship_request_id: string;
  thread_id: string;
  proposed_by: string;
  duration_minutes: number;
  note: string | null;
  availability: string | null;
  status: "proposed" | "accepted" | "declined" | "cancelled";
  responded_at: string | null;
  declined_by: string | null;
  decline_note: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  zoom_meeting_id: string | null;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch pending immediate meeting for a thread
 * Returns the most recent "proposed" immediate meeting
 */
export function usePendingImmediateMeeting(threadId?: string) {
  return useQuery({
    queryKey: ["pending-immediate-meeting", threadId],
    queryFn: async () => {
      if (!threadId) return null;

      const { data, error } = await supabase
        .from("immediate_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .eq("status", "proposed")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[usePendingImmediateMeeting] Error:", error);
        throw error;
      }

      console.log("[usePendingImmediateMeeting] Result:", data?.id || "none");
      return data as ImmediateMeeting | null;
    },
    enabled: !!threadId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Fetch accepted immediate meeting for a thread
 * Returns the most recent "accepted" immediate meeting with Zoom link
 */
export function useAcceptedImmediateMeeting(threadId?: string) {
  return useQuery({
    queryKey: ["accepted-immediate-meeting", threadId],
    queryFn: async () => {
      if (!threadId) return null;

      const { data, error } = await supabase
        .from("immediate_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .eq("status", "accepted")
        .not("zoom_join_url", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[useAcceptedImmediateMeeting] Error:", error);
        throw error;
      }

      console.log("[useAcceptedImmediateMeeting] Result:", data?.id || "none");
      return data as ImmediateMeeting | null;
    },
    enabled: !!threadId,
    staleTime: 0,
  });
}

/**
 * Fetch all immediate meetings for a thread (for history)
 */
export function useImmediateMeetings(threadId?: string) {
  return useQuery({
    queryKey: ["immediate-meetings", threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data, error } = await supabase
        .from("immediate_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ImmediateMeeting[];
    },
    enabled: !!threadId,
  });
}

/**
 * Propose an immediate meeting (creates entry in immediate_meetings table)
 */
export function useProposeImmediateMeeting() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mentorshipRequestId,
      threadId,
      duration,
      note,
      availability,
    }: {
      mentorshipRequestId: string;
      threadId: string;
      duration: number;
      note?: string;
      availability?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      console.log("[useProposeImmediateMeeting] Creating immediate meeting:", {
        mentorshipRequestId,
        threadId,
        duration,
      });

      const { data, error } = await supabase
        .from("immediate_meetings")
        .insert({
          mentorship_request_id: mentorshipRequestId,
          thread_id: threadId,
          proposed_by: user.id,
          duration_minutes: duration,
          note: note || null,
          availability: availability || null,
          status: "proposed",
        })
        .select()
        .single();

      if (error) {
        console.error("[useProposeImmediateMeeting] Error:", error);
        throw error;
      }

      console.log("[useProposeImmediateMeeting] Created:", data.id);
      return data as ImmediateMeeting;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pending-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["immediate-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["can-request-immediate-meeting", variables.threadId] });
      toast.success("Meeting proposal sent!");
    },
    onError: (error) => {
      toast.error("Failed to propose meeting: " + error.message);
    },
  });
}

/**
 * Accept an immediate meeting (creates Zoom meeting and updates status)
 */
export function useAcceptImmediateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      threadId,
      mentorshipRequestId,
    }: {
      meetingId: string;
      threadId: string;
      mentorshipRequestId: string;
    }) => {
      console.log("[useAcceptImmediateMeeting] Accepting:", meetingId);

      // Get meeting duration
      const { data: meeting, error: fetchError } = await supabase
        .from("immediate_meetings")
        .select("duration_minutes")
        .eq("id", meetingId)
        .single();

      if (fetchError) throw fetchError;

      const duration = meeting?.duration_minutes || 30;

      // Create Zoom meeting - edge function will use "immediate" flow (no slot_id)
      const zoomResponse = await supabase.functions.invoke("create-zoom-meeting", {
        body: {
          duration,
          immediate_meeting_id: meetingId,
          mentorship_request_id: mentorshipRequestId,
          // No slot_id = immediate meeting flow
        },
      });

      if (zoomResponse.error) {
        console.error("[useAcceptImmediateMeeting] Zoom creation failed:", zoomResponse.error);
        throw new Error("Failed to create Zoom meeting. Please try again.");
      }

      // Check for application-level errors
      if (zoomResponse.data?.error) {
        throw new Error(zoomResponse.data.error);
      }

      // Update immediate meeting with Zoom details
      const { error: updateError } = await supabase
        .from("immediate_meetings")
        .update({
          status: "accepted",
          responded_at: new Date().toISOString(),
          zoom_meeting_id: zoomResponse.data?.meeting_id || null,
          zoom_join_url: zoomResponse.data?.join_url || null,
          zoom_start_url: zoomResponse.data?.start_url || null,
        })
        .eq("id", meetingId);

      if (updateError) {
        console.error("[useAcceptImmediateMeeting] Update failed:", updateError);
        throw updateError;
      }

      console.log("[useAcceptImmediateMeeting] Success:", {
        meetingId,
        zoomUrl: zoomResponse.data?.join_url,
      });

      return {
        meetingId,
        zoomJoinUrl: zoomResponse.data?.join_url,
        zoomStartUrl: zoomResponse.data?.start_url,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pending-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["accepted-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["immediate-meetings", variables.threadId] });
      toast.success("Meeting accepted! Zoom link created.");
    },
    onError: (error) => {
      toast.error("Failed to accept meeting: " + error.message);
    },
  });
}

/**
 * Decline an immediate meeting
 */
export function useDeclineImmediateMeeting() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      threadId,
      declineNote,
    }: {
      meetingId: string;
      threadId: string;
      declineNote?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      console.log("[useDeclineImmediateMeeting] Declining:", meetingId);

      const { error } = await supabase
        .from("immediate_meetings")
        .update({
          status: "declined",
          responded_at: new Date().toISOString(),
          declined_by: user.id,
          decline_note: declineNote || null,
        })
        .eq("id", meetingId);

      if (error) throw error;

      return { meetingId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pending-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["immediate-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["can-request-immediate-meeting", variables.threadId] });
      toast.success("Meeting declined.");
    },
    onError: (error) => {
      toast.error("Failed to decline meeting: " + error.message);
    },
  });
}

/**
 * Cancel an immediate meeting (also deletes Zoom meeting if exists)
 */
export function useCancelImmediateMeeting() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      threadId,
      zoomMeetingId,
    }: {
      meetingId: string;
      threadId: string;
      zoomMeetingId?: string | null;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      console.log("[useCancelImmediateMeeting] Cancelling:", meetingId);

      // Delete Zoom meeting if exists
      if (zoomMeetingId) {
        try {
          const deleteResponse = await supabase.functions.invoke("delete-zoom-meeting", {
            body: { meeting_id: zoomMeetingId },
          });
          if (deleteResponse.error) {
            console.error("Failed to delete Zoom meeting:", deleteResponse.error);
          }
        } catch (err) {
          console.error("Error calling delete-zoom-meeting:", err);
        }
      }

      // Update status
      const { error } = await supabase
        .from("immediate_meetings")
        .update({
          status: "cancelled",
          cancelled_by: user.id,
          cancelled_at: new Date().toISOString(),
        })
        .eq("id", meetingId);

      if (error) throw error;

      return { meetingId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pending-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["accepted-immediate-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["immediate-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["can-request-immediate-meeting", variables.threadId] });
      toast.success("Meeting cancelled.");
    },
    onError: (error) => {
      toast.error("Failed to cancel meeting: " + error.message);
    },
  });
}

/**
 * Check if user can request another immediate meeting (anti-spam)
 * Separate from scheduled meeting checks
 */
export function useCanRequestImmediateMeeting(threadId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["can-request-immediate-meeting", threadId, user?.id],
    queryFn: async () => {
      if (!threadId || !user?.id) return { canRequest: false, reason: "not_authenticated" };

      // Check for RECENT pending proposals (within last 24 hours)
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);

      const { data: pending } = await supabase
        .from("immediate_meetings")
        .select("id")
        .eq("thread_id", threadId)
        .eq("status", "proposed")
        .gte("created_at", oneDayAgo.toISOString())
        .limit(1);

      if (pending && pending.length > 0) {
        return { canRequest: false, reason: "pending_exists" };
      }

      // Check for recent declines (within 7 days) by same proposer
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data: recentDeclines } = await supabase
        .from("immediate_meetings")
        .select("id")
        .eq("thread_id", threadId)
        .eq("proposed_by", user.id)
        .eq("status", "declined")
        .gte("responded_at", sevenDaysAgo.toISOString())
        .limit(1);

      if (recentDeclines && recentDeclines.length > 0) {
        return { canRequest: false, reason: "recently_declined" };
      }

      return { canRequest: true, reason: null };
    },
    enabled: !!threadId && !!user?.id,
  });
}
